import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import updateConditionActiveOptionOperation from 'editor/src/store/design/operation/updateConditionActiveOptionOperation';
import updateTextConditionVariantsOperation from 'editor/src/store/design/operation/updateTextConditionVariantsOperation';
import getDesignConditions from 'editor/src/store/design/selector/getDesignConditions';
import { Condition, MediaText } from 'editor/src/store/design/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import useTr from 'editor/src/util/useTr';

import IconText from 'editor/src/component/Icon/IconText';
import SwitchControl from 'editor/src/component/SwitchControl';

import OptionsDropDown from '../ElementsSection/OptionsDropDown';

import styles from './index.module.scss';

interface Props {
  spreadIndex: number;
  condition: Condition;
  /** Whether the host enables per-option text AUTHORING. False means the
   *  section is shown only because the design already carries variants — the
   *  capture path in `updateMediaElementOperation` is gated on the same
   *  setting, so canvas edits would be silently discarded. Existing content
   *  stays visible and can still be switched off; nothing new can be switched
   *  on. See ConditionSettings. */
  authoringEnabled: boolean;
}

interface SpreadGroup {
  spreadIndex: number;
  spreadName: string;
  elements: MediaText[];
}

/**
 * Option-centric authoring for texts whose CONTENT changes with this picklist
 * (as opposed to elements that appear/disappear, which live in "Associated
 * elements"). The author switches a text on here once; from then on, picking
 * an option and editing that text on the canvas stores the edit for that
 * option.
 *
 * The texts on offer follow the picklist's own scope — its page, or the whole
 * design once it is shown on every page, because then a variant may sit on the
 * other side of the card from the dropdown that drives it. See `groups`.
 */
function VaryingTextsSection({ condition, spreadIndex, authoringEnabled }: Props) {
  const dispatch = useDispatch();
  const tr = useTr();

  const spreads = useSelector((state) => state.design.designData?.spreads, shallowEqual);
  const designConditions = useSelector(getDesignConditions, shallowEqual);

  const isVarying = useCallback(
    (element: MediaText) => element.extra.textVariants?.conditionId === condition.id,
    [condition.id],
  );

  // Texts this picklist can drive, grouped by the page they sit on.
  //
  // A picklist the buyer only sees on its own page should only offer texts on
  // that page: driving a label the buyer cannot see the control for is a trap,
  // not a feature. Design-scoped (showOnAllSpreads) lifts that and offers the
  // whole design, which is the point of the scope.
  //
  // A text that ALREADY follows this picklist is listed wherever it lives, so
  // scoping back down never strands variant data with no way to switch it off.
  const groups = useMemo<SpreadGroup[]>(
    () =>
      (spreads ?? [])
        .map((spread, index) => ({
          spreadIndex: index,
          spreadName: spread.name,
          elements: (spread.pages[0]?.groups.media ?? [])
            .filter((element): element is MediaText => element.type === 'text')
            .filter((element) => condition.showOnAllSpreads || index === spreadIndex || isVarying(element)),
        }))
        .filter((group) => group.elements.length > 0),
    [spreads, condition.showOnAllSpreads, spreadIndex, isVarying],
  );

  // A text can only follow one picklist. When it already follows a DIFFERENT
  // one, the row has to say so: an "off" switch would invite a click that
  // silently reassigns the text and drops the other picklist's translations.
  //
  // An id no condition in the design answers to is treated as unassigned
  // instead. deleteConditionReducer clears these on the way out, but a design
  // saved before it did would otherwise show the row as owned by a raw uuid
  // with the switch disabled — taken by a picklist that no longer exists, and
  // no way to reassign it.
  const ownedByOtherPicklist = useCallback(
    (element: MediaText): string | undefined => {
      const { conditionId } = element.extra.textVariants ?? {};
      if (!conditionId || conditionId === condition.id) {
        return undefined;
      }
      return designConditions.find((other) => other.id === conditionId)?.name;
    },
    [condition.id, designConditions],
  );

  const onToggle = useCallback(
    (element: MediaText, on: boolean) => {
      if (!on) {
        dispatch(updateTextConditionVariantsOperation(element.uuid, undefined));
        return;
      }
      // Switching ON without the capture path is a trap: it seeds a variant the
      // author then cannot edit. Switching OFF stays open either way, so the
      // data is always undoable. The switch is disabled too — this guards the
      // operation itself.
      if (!authoringEnabled) {
        return;
      }
      // Seed the active option with what is on the canvas right now, so the
      // author's starting language is captured without retyping it.
      dispatch(
        updateTextConditionVariantsOperation(element.uuid, {
          conditionId: condition.id,
          values: {
            [condition.activeOptionId]: {
              text: element.extra.text,
              fontFamily: element.extra.fontFamily,
            },
          },
        }),
      );
    },
    [condition.id, condition.activeOptionId, authoringEnabled],
  );

  const setActiveOptionId = useCallback(
    (optionId: string) => {
      dispatch(updateConditionActiveOptionOperation({ spreadIndex, conditionId: condition.id }, optionId));
    },
    [spreadIndex, condition.id],
  );

  // One page's worth of texts needs no page heading.
  const showSpreadHeadings = groups.length > 1;
  const varyingCount = groups.reduce(
    (total, group) => total + group.elements.filter((element) => isVarying(element)).length,
    0,
  );

  function renderElement(element: MediaText) {
    const on = isVarying(element);
    const otherPicklistName = ownedByOtherPicklist(element);
    const variant = element.extra.textVariants?.values[condition.activeOptionId];

    let secondaryLine: string;
    if (otherPicklistName) {
      secondaryLine = tr('editor-text-variants-other-picklist', 'follows “{{name}}”', {
        name: otherPicklistName,
      });
    } else if (on) {
      secondaryLine = variant?.text ?? tr('editor-text-variants-not-set', 'not set yet');
    } else {
      secondaryLine = element.extra.text;
    }

    return (
      <div
        key={element.uuid}
        className={cn(styles.element, 'cy-varying-texts-element', {
          [styles.on]: on,
          [styles.taken]: !!otherPicklistName,
        })}
      >
        <div className={styles.icon}>
          <IconText />
        </div>
        <div className={styles.label}>
          <div className={styles.name}>{element.name}</div>
          <div
            className={cn(styles.value, {
              [styles.missing]: (on && !variant) || !!otherPicklistName,
            })}
          >
            {secondaryLine}
          </div>
        </div>
        <SwitchControl
          title=""
          className="cy-varying-text-switch"
          on={on}
          // Read-only host: an already-following text keeps its switch so the
          // author can turn it OFF, but a text that is not following one yet
          // has its switch closed — turning it on would seed content the
          // capture path cannot then edit.
          disabled={!!otherPicklistName || (!authoringEnabled && !on)}
          onSwitch={(value) => onToggle(element, value)}
        />
      </div>
    );
  }

  return (
    <div className={cn(styles.VaryingTextsSection, 'cy-varying-texts-section')}>
      <OptionsDropDown
        options={condition.options}
        activeOptionId={condition.activeOptionId}
        selectOption={setActiveOptionId}
      />
      <div className={cn(styles.note, { [styles.noteReadOnly]: !authoringEnabled })}>
        {authoringEnabled
          ? tr(
              'editor-condition-varying-texts-note',
              'Switch a text on, pick an option above, then edit that text on the canvas — each option keeps its own text and font.',
            )
          : tr(
              'editor-condition-varying-texts-readonly',
              'This design already keeps a separate text per option. Editing it is not enabled here — you can see what each option holds, and switch a text off to remove it.',
            )}
      </div>
      {groups.length > 0 ? (
        <div className={cn(styles.elements, 'cy-varying-texts-elements')}>
          {groups.map((group) => (
            <div key={group.spreadIndex} className={cn(styles.spreadGroup, 'cy-varying-texts-spread-group')}>
              {showSpreadHeadings && (
                <div className={styles.spreadHeading}>{group.spreadName || `#${group.spreadIndex + 1}`}</div>
              )}
              {group.elements.map(renderElement)}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.empty}>
          {condition.showOnAllSpreads
            ? tr('editor-condition-varying-texts-empty', 'No text layers in this design yet.')
            : tr('editor-condition-varying-texts-empty-page', 'No text layers on this page yet.')}
        </div>
      )}
      {varyingCount > 0 && (
        <div className={styles.count}>
          {tr('editor-condition-varying-texts-count', '{{count}} text(s) follow this picklist', {
            count: varyingCount,
          })}
        </div>
      )}
    </div>
  );
}

export default React.memo(VaryingTextsSection);
